import { useState, useCallback, useMemo, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

// CONSTANTS
import { FLASH_MESSAGE_TYPES } from 'constants/common';

// ASSETS
import LegalIcon from 'assets/LegalIcon';

// COMPONENTS
import FlashMessage from 'components/FlashMessage';
import { PrimaryButton } from 'components/Buttons';

// HOOKS
import { useAppDispatch } from 'hooks/store';

// SELECTORS
import { selectDealershipSettings, selectLoading } from 'services/DealershipSettings/selectors';

// THUNKS
import { acceptPaymentTerms, getDealershipSettings } from 'services/DealershipSettings/thunks';

// STYLES
import { Description } from './styles';
import { Section, SectionHeader, SectionContent, SectionHeaderTitle } from 'styles/cards';
import colors from 'styles/colors';

// UTILS
import { getBrandingTranslationKey } from 'utils/branding.utils';
import { selectSelectedHierarchyEntity } from 'services/Hierarchy/selectors';

const FleetSettingsPaymentTermsView = () => {
  const [flashMessageVisible, setFlashMessageVisible] = useState(false);
  const [flashMessageType, setFlashMessageType] = useState(null);
  const [flashMessage, setFlashMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const intl = useIntl();
  const dispatch = useAppDispatch();
  const settings = useSelector(selectDealershipSettings());
  const isLoading = useSelector(selectLoading());
  const selectedHierarchyEntity = useSelector(selectSelectedHierarchyEntity());
  useEffect(() => {
    dispatch(getDealershipSettings());
  }, [dispatch, selectedHierarchyEntity]);

  const getPaymentsEnabled = useMemo(
    () => settings?.get('paymentAccountId') && settings?.get('paymentsEnabled'),
    [settings]
  );

  const getTermsAccepted = useMemo(() => settings?.get('paymentsAcceptedTerms'), [settings]);

  const handleOpenFlashMessage = (text, type) => {
    setFlashMessageVisible(true);
    setFlashMessageType(type);
    setFlashMessage(text);
  };

  const handleCloseFlashMessage = () => {
    setFlashMessageVisible(false);
    setFlashMessageType(null);
    setFlashMessage(null);
  };

  const handleRegister = useCallback(() => {
    setLoading(true);

    dispatch(acceptPaymentTerms(settings.get('id')))
      .then(() => {
        handleOpenFlashMessage(
          intl.formatMessage({ id: 'views.FleetSettings.Payment.termsSuccessfullyAccepted' }),
          FLASH_MESSAGE_TYPES.success
        );
        setLoading(false);
      })
      .catch(() => {
        handleOpenFlashMessage(intl.formatMessage({ id: 'errors.unspecified' }), FLASH_MESSAGE_TYPES.error);
        setLoading(false);
      });
  }, [dispatch, settings, intl]);

  const getContextMessage = () => {
    if (getPaymentsEnabled) {
      return getTermsAccepted ? (
        <Description>
          <FormattedMessage
            id={`views.FleetSettings.Payment.${getBrandingTranslationKey(settings.get('brandingType'), 'tosAccepted')}`}
          />
        </Description>
      ) : (
        <Description>
          <FormattedMessage
            id={`views.FleetSettings.Payment.${getBrandingTranslationKey(
              settings.get('brandingType'),
              'paymentsEnabled'
            )}`}
          />
        </Description>
      );
    } else {
      return (
        <Description>
          <b>
            <FormattedMessage
              id={`views.FleetSettings.Payment.${getBrandingTranslationKey(
                settings.get('brandingType'),
                'paymentsNotEnabled1'
              )}`}
            />
          </b>
          <br />
          <FormattedMessage
            id="views.FleetSettings.Payment.paymentsNotEnabled2"
            values={{ link: <a href="mailto:team@fleetlane.com">team@fleetlane.com</a> }}
          />
        </Description>
      );
    }
  };

  return (
    <Section>
      <FlashMessage
        visible={flashMessageVisible}
        type={flashMessageType}
        text={flashMessage}
        onClose={handleCloseFlashMessage}
      />
      <SectionHeader>
        <SectionHeaderTitle>
          <LegalIcon color={colors.fleetBlue} />
          <FormattedMessage id="views.FleetSettings.PaymentTerms.title" />
        </SectionHeaderTitle>
      </SectionHeader>
      <SectionContent>
        {getContextMessage()}
        <PrimaryButton
          onClick={handleRegister}
          hidden={!getPaymentsEnabled}
          disabled={getTermsAccepted || loading || isLoading}
          className={getTermsAccepted ? 'green' : ''}
        >
          <FormattedMessage
            id={
              getTermsAccepted
                ? 'views.FleetSettings.Payment.accountRegistered'
                : 'views.FleetSettings.Payment.registerAccount'
            }
          />
        </PrimaryButton>
        {getPaymentsEnabled && (
          <Description>
            <FormattedMessage
              id="views.FleetSettings.Payment.registerAccountDescription"
              values={{
                link: (
                  <a href="https://stripe.com/ca/connect-account/legal" target="_blank" rel="noopener noreferrer">
                    <FormattedMessage id="views.FleetSettings.Payment.stripeTerms" />
                  </a>
                ),
              }}
            />
          </Description>
        )}
      </SectionContent>
    </Section>
  );
};

export default FleetSettingsPaymentTermsView;
